import { useState, useEffect } from 'react';
import dayjs from 'dayjs';

import * as URLS from '../constants/urls';
import * as api from '../utils/api';

const ITEM_PER_PAGE = 3;

const useNewsStockMarket = () => {
  const [isNewsLoading, setNewsLoading] = useState(true);
  const [totalPage, setTotalPage] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [news, setNews] = useState([]);
  const [isNewsSourceLoading, setNewsSourceLoading] = useState(false);
  const [newsSource, setNewsSource] = useState([]);
  const [selectedNewsSource, setSelectedNewsSource] = useState(null);
  const [offsets, setOffsets] = useState(null);
  const [newsConfig, setNewsConfig] = useState(null);
  const [keyword, setKeyword] = useState('');
  const [publishedAt, setPublishedAt] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isPublishLoading, setPublishLoading] = useState(false);

  const fetchNews = async (options) => {
    setNewsLoading(true);
    try {
      const { data } = await api.post(URLS.GET_ALL_NEWS_BY_SOURCE_URL, {
        limit: ITEM_PER_PAGE,
        offset: ITEM_PER_PAGE * (options.currentPage - 1),
        startDate: options.startDate,
        endDate: options.endDate,
        keyword: options.keyword,
        source: options.source || -1,
      });
      let newsBySource = {};
      let newsOffsets = {};
      newsSource?.forEach((item) => {
        const news = data.news.filter((n) => n.source === item.value);
        if (news.length) {
          newsBySource = { ...newsBySource, [item.value]: news };
          newsOffsets = {
            ...newsOffsets,
            [item.value]: offsets[item.value] + news.length,
          };
        }
      });
      if (Object.keys(newsOffsets).length) {
        setOffsets(newsOffsets);
        const item = newsConfig.find((n) => n.page === currentPage + 1);
        if (!item) {
          setNewsConfig([
            ...newsConfig,
            { page: currentPage + 1, offsets: { ...newsOffsets } },
          ]);
        }
      }
      const getCompanies = (stocks) => {
        if (!stocks) {
          return;
        }
        const parseStocks = JSON.parse(stocks);
        const companies = [];
        for (const stock of parseStocks) {
          const company = data.companies.find((c) => c.machungkhoan === stock);
          if (company) {
            companies.push(company);
          }
        }
        return companies;
      };
      const getNewsList = (news) => {
        const results = [];
        const dates = [];
        news.forEach((item) => {
          const date = dayjs(Number(item.publishedAt)).format('DD/MM/YYYY');
          if (!dates.includes(date)) {
            dates.push(date);
            const _news = news.filter(
              (n) => dayjs(Number(n.publishedAt)).format('DD/MM/YYYY') === date
            );
            results.push({
              date,
              news: _news.map((n) => ({
                ...n,
                companies: getCompanies(n.machungkhoan),
              })),
            });
          }
        });
        return results;
      };
      setNews(getNewsList(data.news));
      if (currentPage === 1) {
        setTotalPage(Math.ceil(data.totalItems / ITEM_PER_PAGE));
      }
    } catch (error) {
      console.error(error);
    }
    setNewsLoading(false);
  };

  const fetchNewsSource = async () => {
    setNewsSourceLoading(true);
    try {
      const { data } = await api.get(URLS.GET_ALL_SOURCE_OF_NEWS);
      const totalNews = data.source.reduce(
        (total, current) => total + current.total,
        0
      );
      const options = [
        {
          value: '-1',
          label: 'Tất cả',
          total: totalNews,
        },
      ];
      let newsOffsets = {};
      for (let i = 0; i < data.source.length; i++) {
        options.push({
          value: data.source[i].source,
          label: data.source[i].source,
          total: data.source[i].total,
        });
        newsOffsets = { ...newsOffsets, [data.source[i].source]: 0 };
      }
      setOffsets(newsOffsets);
      setNewsConfig([
        {
          page: 1,
          offsets: {
            ...newsOffsets,
          },
        },
      ]);
      setNewsSource([...options]);
      setTimeout(() => {
        setSelectedNewsSource(options[0]);
      }, 200);
    } catch (error) {
      console.error(error);
    }
    setNewsSourceLoading(false);
  };

  const fetchLatestPublish = async () => {
    setPublishLoading(true);
    try {
      const { data } = await api.get(URLS.ADMIN_GET_LATEST_PUBLISH_DATE_NEWS);
      setPublishedAt(data?.publishedAt);
    } catch (error) {
      console.error(error);
    }
    setPublishLoading(false);
  };

  useEffect(() => {
    fetchNewsSource();
    fetchLatestPublish();
  }, []);

  useEffect(() => {
    if (newsSource.length) {
      fetchNews({
        currentPage,
        startDate,
        endDate,
        keyword,
        source: selectedNewsSource?.value || '-1',
        currentPage,
      });
    }
  }, [newsSource]);

  const handleNewsSourceChange = (value) => {
    setCurrentPage(1);
    setSelectedNewsSource(value);
  };

  const handleDateChange = (_startDate, _endDate) => {
    const startDateValue = dayjs(_startDate).hour(0).minute(0).second(1);
    const endDateValue = dayjs(_endDate || _startDate)
      .hour(23)
      .minute(59)
      .second(59);

    setStartDate(startDateValue);
    setEndDate(endDateValue);
  };

  const handleFilter = async () => {
    fetchNews({
      currentPage,
      startDate,
      endDate,
      keyword,
      source: selectedNewsSource?.value || '-1',
      currentPage,
    });
  };

  const handlePageChange = async (page) => {
    setCurrentPage(page);
    fetchNews({
      currentPage,
      startDate,
      endDate,
      keyword,
      source: selectedNewsSource?.value || '-1',
      currentPage: page,
    });
  };

  return {
    isNewsLoading,
    totalPage,
    currentPage,
    setCurrentPage,
    news,
    isNewsSourceLoading,
    newsSource,
    selectedNewsSource,
    handleNewsSourceChange,
    keyword,
    setKeyword,
    publishedAt,
    startDate,
    endDate,
    handleDateChange,
    handleFilter,
    handlePageChange,
  };
};

export default useNewsStockMarket;
